import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {CheckCircle, Sparkle, UsersThree, Warning} from '../../icons';
import {Spinner} from '../../Spinner';
import {PortalSparkline} from '../../portal_usage/PortalSparkline';
import {clientQueryCount, daysSince, USAGE_STATE} from '../../portal_usage/portalUsageState';
import {relativeTimeLabel} from './panelFormatters';
import './RailCards.scss';

/** El usuario que entró más recientemente (los que nunca entraron van último). */
export function mostRecentUser(users) {
    const list = (users || []).filter(Boolean);
    if (!list.length) return null;
    return list.reduce((best, user) => {
        if (!user.last_login_at) return best;
        if (!best.last_login_at) return user;
        return new Date(user.last_login_at) > new Date(best.last_login_at) ? user : best;
    }, list[0]);
}

/**
 * Card "Portal del cliente" (`panel.portal`): el acceso de solo lectura que la agencia le da a su
 * cliente, con su marca.
 *
 * Cuatro escenarios, en el orden en que los vive una agencia:
 * 1. Día 1 — nadie invitado: se explica QUÉ recibe el cliente y se ofrece invitarlo.
 * 2. Invitación enviada y sin aceptar: a quién y cuándo, para poder recordárselo.
 * 3. Activo: cuánto consulta y cuándo fue la última vez.
 * 4. **Dormido**: usó y dejó de usar. Es el que faltaba, y es el que dispara la acción comercial.
 *
 * El cuarto no existía y no era un olvido de contenido: hasta ahora la card resolvía el estado con
 * `relativeTimeLabel`, que **no juzga antigüedad**. "Hace 74 días" salía con el mismo tilde verde
 * que "hace 2 horas", así que el cliente al que hay que llamar hoy se mostraba como un logro. El
 * veredicto ahora lo manda el backend (`portal.usage.state`), que es el mismo que usan el detalle
 * y el ranking cross-cliente: un KPI que vive en tres pantallas se define una vez o las tres
 * divergen.
 *
 * **`usage` ausente ⇒ la card se comporta exactamente como antes.** El backend y el frontend no
 * deployan juntos, así que el bloque nuevo es aditivo y el viejo sigue siendo el camino por
 * defecto — incluido `mcp_last_query_at`, que se mantiene por compatibilidad.
 */
export function PortalCard({data, loading, clientId}) {
    const {t} = useTranslation();

    if (loading) {
        return (
            <div className="box pnl-card">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    if (!data) return null;

    const users = data.users || [];
    const invitations = data.pending_invitations || [];
    const hasUsers = users.length > 0;
    const usage = data.usage || null;
    const configUrl = `/client/${clientId}/portal-config`;
    // El detalle vive dentro del tab Portal, debajo de los usuarios: mismo destino, otra promesa.
    const detailLink = (
        <Link className="pnl-card__link" to={configUrl}>
            {t('client_panel.portal_see_detail', 'Ver el detalle')} &rarr;
        </Link>
    );

    const renderUsage = () => {
        const state = usage.state;
        // Consultas reales, no tool calls. La card NO muestra los pasos: contesta «¿lo está
        // usando?» y el matiz cuesta más de lo que explica en tres líneas — el detalle está a un
        // click con la explicación completa. Lo que no se negocia es que el número cambie: si el
        // Panel dijera 33 y el detalle 6, se rompe la regla de que un KPI que vive en tres
        // pantallas se define una sola vez.
        const clientQueries = clientQueryCount(usage);
        const lastLabel = relativeTimeLabel(usage.last_activity_at, t);
        const usersLine = (
            <p className="pnl-portal__line -muted">
                <UsersThree size={15}/>
                {users.length === 1
                    ? t('client_panel.portal_users_one', '1 usuario con acceso')
                    : t('client_panel.portal_users_many', '{{n}} usuarios con acceso', {n: users.length})}
            </p>
        );

        // --- Dormido: usó y dejó de usar ------------------------------------------------------
        if (state === USAGE_STATE.DORMANT) {
            const days = daysSince(usage.last_activity_at);
            return (
                <>
                    <p className="pnl-portal__line -warn">
                        <Warning size={15} weight="fill"/>
                        {t('client_panel.portal_dormant', 'Sin actividad hace {{n}} días', {n: days})}
                    </p>
                    <PortalSparkline weeks={usage.weeks}/>
                    <p className="pnl-portal__line -muted">
                        {t('client_panel.portal_dormant_last', 'La última vez fue {{when}}',
                            {when: lastLabel || '—', interpolation: {escapeValue: false}})}
                    </p>
                    {usersLine}
                    <p className="pnl-portal__foot">{detailLink}</p>
                </>
            );
        }

        // --- Activo ---------------------------------------------------------------------------
        if (state === USAGE_STATE.ACTIVE) {
            return (
                <>
                    <p className="pnl-portal__line -ok">
                        <CheckCircle size={15} weight="fill"/>
                        {t('client_panel.portal_active_usage',
                            'Activo — {{queries}} consultas en {{days}} días', {
                                queries: clientQueries,
                                days: usage.window_days,
                            })}
                    </p>
                    <PortalSparkline weeks={usage.weeks}/>
                    <p className="pnl-portal__line -muted">
                        <Sparkle size={15} weight="fill"/>
                        {t('client_panel.portal_last_activity', 'Última actividad: {{when}}',
                            {when: lastLabel || '—', interpolation: {escapeValue: false}})}
                    </p>
                    <p className="pnl-portal__foot">{detailLink}</p>
                </>
            );
        }

        // --- El asistente apagado --------------------------------------------------------------
        // Distinto de "todavía no conectó su IA": acá la decisión fue de la agencia, no del cliente.
        if (state === USAGE_STATE.MODULE_OFF) {
            return (
                <>
                    <p className="pnl-portal__line -muted">
                        {t('client_panel.portal_module_off', 'El asistente está apagado para este cliente.')}
                    </p>
                    {usersLine}
                    {/* Sin link en el pie: la cabecera ya lleva "Gestionar" y es exactamente el
                        mismo destino. Repetirlo no da una segunda opción, sólo ruido. */}
                </>
            );
        }

        // --- Con acceso y sin estrenar ----------------------------------------------------------
        return (
            <>
                <p className="pnl-portal__line -muted">
                    {t('client_panel.portal_invited_unused', 'Con acceso, todavía sin usar')}
                </p>
                {usersLine}
                <p className="pnl-portal__foot">{detailLink}</p>
            </>
        );
    };

    const renderBody = () => {
        // --- Con `usage`: el estado lo decide el backend ------------------------------------------
        if (hasUsers && usage && usage.state) return renderUsage();

        // --- Sin `usage` (backend viejo): exactamente el comportamiento anterior -------------------
        if (hasUsers) {
            const user = mostRecentUser(users);
            const lastLogin = user && relativeTimeLabel(user.last_login_at, t);
            const mcpAt = relativeTimeLabel(data.mcp_last_query_at, t);

            return (
                <>
                    {lastLogin ? (
                        <p className="pnl-portal__line -ok">
                            <CheckCircle size={15} weight="fill"/>
                            {t('client_panel.portal_active', 'Activo — {{name}} entró {{when}}', {
                                name: user.name || '',
                                when: lastLogin,
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                    ) : (
                        // Invitación aceptada pero sin un solo ingreso: no es un logro ni un error.
                        <p className="pnl-portal__line -muted">
                            {t('client_panel.portal_never_entered', 'Invitado — todavía no entró')}
                        </p>
                    )}

                    {mcpAt ? (
                        <p className="pnl-portal__line -mcp">
                            <Sparkle size={15} weight="fill"/>
                            {t('client_panel.portal_mcp_used', 'Usa el MCP — última consulta con su IA: {{when}}', {
                                when: mcpAt,
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                    ) : (
                        <p className="pnl-portal__line -muted">
                            {t('client_panel.portal_mcp_never', 'Todavía no conectó su IA.')}
                        </p>
                    )}

                    <p className="pnl-portal__foot">
                        {t('client_panel.portal_value',
                            'Tu cliente ve sus números en su portal y le pregunta a su propia IA.')}
                    </p>
                </>
            );
        }

        // --- Invitación enviada, todavía sin aceptar ----------------------------------------------
        if (invitations.length > 0) {
            const invitation = invitations[0];
            const sentAt = relativeTimeLabel(invitation.sent_at, t);

            return (
                <>
                    {/* El mail va en un <strong> aparte y NO interpolado dentro de la frase: es un
                        dato cargado por un humano y no se pinta con dangerouslySetInnerHTML. */}
                    <p className="pnl-portal__line -muted">
                        {t('client_panel.portal_invitation_sent', 'Invitación enviada a')}{' '}
                        <span className="pnl-portal__mail">{invitation.email}</span>
                        {sentAt ? ` · ${sentAt}` : ''}
                    </p>
                    <Link className="pnl-card__link" to={configUrl}>
                        {t('client_panel.portal_manage', 'Gestionar')} &rarr;
                    </Link>
                </>
            );
        }

        // --- Día 1: nadie invitado -----------------------------------------------------------------
        return (
            <>
                <p className="pnl-portal__pitch">
                    {t('client_panel.portal_pitch',
                        'Dale a tu cliente su propio acceso de solo lectura, con tu marca.')}
                </p>
                <Link className="pnl-btn pnl-btn--primary" to={configUrl}>
                    {t('client_panel.portal_invite', 'Invitar al portal')}
                </Link>
            </>
        );
    };

    // El borde ámbar es la señal que se lee sin leer: en el rail, la card del cliente que se apagó
    // tiene que distinguirse de las otras antes de que el usuario llegue al texto.
    const isDormant = hasUsers && usage && usage.state === USAGE_STATE.DORMANT;

    return (
        <div className={`box pnl-card${isDormant ? ' -warn' : ''}`}>
            <div className="pnl-card__head">
                <h2 className="pnl-card__title">{t('client_panel.portal_title', 'Portal del cliente')}</h2>
                {hasUsers && (
                    <>
                        <span className="pnl-card__spacer"/>
                        <Link className="pnl-card__link" to={configUrl}>
                            {t('client_panel.portal_manage', 'Gestionar')} &rarr;
                        </Link>
                    </>
                )}
            </div>

            <div className="pnl-cnt">{renderBody()}</div>
        </div>
    );
}
